import { React, useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box, Backdrop, CircularProgress, Paper, Button, Dialog, DialogActions, DialogContent, DialogContentText, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, Grid, Alert
} from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import LinkIcon from '@mui/icons-material/Link';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    DataGridPro,
    GridToolbar
} from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import axios from 'axios';
import dayjs from 'dayjs';

import { socket } from './socket';

var api = axios.create({ withCredentials: true });

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
}).format;

const numberFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
}).format;

// Reconciliation Details Dialog Component
const ReconciliationDialog = ({ open, onClose, reconciliation, batch, onRetry, onTrigger, isRetrying }) => {
    const hasReconciliation = reconciliation && reconciliation.status !== 'not_started';
    const status = hasReconciliation ? reconciliation.status : 'not_started';
    const { triggeredAt, completedAt, summary, amountValidation, results } = reconciliation || {};

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'failed': return 'error';
            case 'partial': return 'warning';
            case 'in_progress': return 'info';
            default: return 'default';
        }
    };

    // Check if batch has PAID payments
    const hasPaidPayments = batch?.payments?.some(p => p.status === 'PAID');

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Reconciliation Details</Typography>
                    <Chip 
                        size="small" 
                        label={status} 
                        color={getStatusColor(status)}
                        sx={{ ml: 2 }}
                    />
                </Box>

                {!hasReconciliation ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            Reconciliation has not been triggered for this batch.
                        </Typography>
                        {batch && (
                            <Typography variant="body2" color="text.secondary">
                                Batch ID: {batch.batchId}
                            </Typography>
                        )}
                        {!hasPaidPayments && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                This batch has no PAID payments yet. Reconciliation requires PAID status.
                            </Alert>
                        )}
                    </Box>
                ) : (
                    <>
                        {/* Timing Info */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Triggered: {triggeredAt ? dayjs(triggeredAt).format('MMM D, YYYY h:mm A') : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Completed: {completedAt ? dayjs(completedAt).format('MMM D, YYYY h:mm A') : 'In Progress'}
                            </Typography>
                        </Box>

                        {/* Amount Validation */}
                        {amountValidation && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Amount Validation</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">Tipalti Paid</Typography>
                                        <Typography variant="h6">{currencyFormatter(amountValidation.tipaltiTotal || 0)}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">PO Total</Typography>
                                        <Typography variant="h6">{currencyFormatter(amountValidation.poTotal || 0)}</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography variant="body2" color="text.secondary">Match</Typography>
                                        <Typography variant="h6">
                                    {amountValidation.matched ? <CheckCircleIcon color="success" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> : <ErrorIcon color="error" sx={{ verticalAlign: 'middle', mr: 0.5 }} />}
                                    {amountValidation.matched ? 'Yes' : `No (${currencyFormatter(amountValidation.difference || 0)})`}
                                </Typography>
                                    </Grid>
                                </Grid>
                                {amountValidation.errors && amountValidation.errors.length > 0 && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                        {amountValidation.errors.map((err, i) => <div key={i}>{err}</div>)}
                                    </Alert>
                                )}
                            </Paper>
                        )}

                        {/* Summary */}
                        {summary && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Total POs</Typography>
                                        <Typography variant="h6">{summary.total}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="success.main">Success</Typography>
                                        <Typography variant="h6" color="success.main">{summary.success}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="error.main">Failed</Typography>
                                        <Typography variant="h6" color="error.main">{summary.failed}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Skipped</Typography>
                                        <Typography variant="h6">{summary.skipped}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Reconciled</Typography>
                                        <Typography variant="h6">{currencyFormatter(summary.reconciledAmount || 0)}</Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {/* Per-PO Results */}
                        {results && results.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Purchase Order Results</Typography>
                                <TableContainer sx={{ maxHeight: 300 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>PO ID</TableCell>
                                                <TableCell>Expected</TableCell>
                                                <TableCell>Posted</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Notes/Error</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {results.map((result, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{result.poId}</TableCell>
                                                    <TableCell>{currencyFormatter(result.expectedAmount || 0)}</TableCell>
                                                    <TableCell>{currencyFormatter(result.postedAmount || 0)}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {result.status === 'success' ? <CheckCircleIcon color="success" fontSize="small" /> : result.status === 'skipped' ? <SkipNextIcon fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
                                                            {result.status}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ color: result.error ? 'error.main' : 'text.secondary', maxWidth: 250 }}>
                                                        {result.error ? (result.error.Message || result.error.message || JSON.stringify(result.error)) : result.note || result.skipReason || '—'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {!hasReconciliation && hasPaidPayments && (
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => onTrigger(batch.batchId)}
                        disabled={isRetrying}
                    >
                        {isRetrying ? 'Triggering...' : 'Trigger Reconciliation'}
                    </Button>
                )}
                {hasReconciliation && (status === 'failed' || status === 'partial') && (
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => onRetry(batch.batchId)}
                        disabled={isRetrying}
                    >
                        {isRetrying ? 'Retrying...' : 'Retry Failed'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

// Alternative Purchase Order Details component using DataGridPro
const PurchaseOrderDetailsGrid = ({ row }) => {
    const purchaseOrders = row.purchaseOrders || [];
    
    // Convert purchase orders to rows for DataGridPro
    const poRows = purchaseOrders.map((po, index) => ({
        id: index,
        poId: po._id,
        externalPoNumber: po.external_po_number,
        poDate: po.po_date ? dayjs(po.po_date).toDate() : null,
        ticketCost: po.po_ticket_cost,
        consignmentFees: po.po_consignment_fees,
        payment: po.po_payment,
        balanceDue: po.po_balance_due
    }));

    // Define columns for the DataGridPro
    const poColumns = [
        { field: 'poId', headerName: 'PO ID', flex: 0.2, minWidth: 80 },
        { field: 'externalPoNumber', headerName: 'External PO #', flex: 0.2, minWidth: 100 },
        { 
            field: 'poDate', 
            headerName: 'PO Date', 
            flex: 0.2, 
            minWidth: 100, 
            type: 'date'
        },
        { 
            field: 'ticketCost', 
            headerName: 'Ticket Cost', 
            flex: 0.15, 
            minWidth: 100,
            type: 'number',
            renderCell: (params) => {
                return `${currencyFormatter(params.value)}`;
            }
        },
        { 
            field: 'consignmentFees', 
            headerName: 'Commission', 
            flex: 0.15, 
            minWidth: 130,
            type: 'number',
            renderCell: (params) => {
                return `${currencyFormatter(params.value)}`;
            }
        },
        { 
            field: 'payment', 
            headerName: 'Payment', 
            flex: 0.15, 
            minWidth: 100,
            type: 'number',
            renderCell: (params) => {
                return `${currencyFormatter(params.value)}`;
            }
        },
        { 
            field: 'balanceDue', 
            headerName: 'Balance Due', 
            flex: 0.15, 
            minWidth: 110,
            type: 'number',
            renderCell: (params) => {
                return `${currencyFormatter(params.value)}`;
            }
        }
    ];

    return (
        <div style={{ height: '100%', width: '100%' }}>
            {purchaseOrders.length > 0 ? (
                <DataGridPro
                    rows={poRows}
                    columns={poColumns}
                    density="compact"
                    disableRowSelectionOnClick
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 100 }
                        }
                    }}
                    pageSizeOptions={[100]}
                    hideFooterPagination={purchaseOrders.length <= 100}
                    sx={{ 
                        height: '100%',
                        border: 'none',
                        '& .MuiDataGrid-virtualScroller': { 
                            minHeight: 250,
                            maxHeight: '100%'
                        }
                    }}
                />
            ) : (
                <Typography variant="body2" sx={{ p: 2 }}>No purchase order details available</Typography>
            )}
        </div>
    );
};

function Commissions(props) {
    const { getAccessTokenSilently } = useAuth0();
    const [broker_loading, setBrokerLoading] = useState(false);
    const [columns, setColumns] = useState(null);
    const [rows, setRows] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [batches, setBatches] = useState([]);
    const [data, setData] = useState([]);
    // State for expanded row IDs
    const [expandedRowIds, setExpandedRowIds] = useState([]);
    // State for reconciliation dialog
    const [reconDialogOpen, setReconDialogOpen] = useState(false);
    const [selectedRecon, setSelectedRecon] = useState(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const user = props.user;
    const isConnected = props.isConnected;

    const handleSelectionChange = (newSelection) => {
        const validSelection = newSelection.filter((rowId) => {
            const row = rows.find((r) => r.id === rowId);
            if (row.totalPoBalanceDue <= 0) {
                toast.error(`Invalid Amount : ${currencyFormatter(row.totalPoBalanceDue)}`);
                return false;
            }
            return true;
        });
        setSelectedRows(validSelection);
    };

    const handleDialogOpen = () => {
        if (selectedRows.length === 0) {
            toast.error('No Payments Selected');
            return;
        }
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const handleSubmit = async () => {
        if (selectedRows.length === 0) {
            toast.error('No Payments Selected');
            return;
        }

        setBrokerLoading(true);
        setOpenDialog(false);
        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            const selectedData = selectedRows.map((rowId) => rows.find((r) => r.id === rowId));

            await api.post(
                `${process.env.REACT_APP_BASEURL}/api/batches/send`,
                { data: selectedData },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Payment Batch Successful.');
            setSelectedRows([]);
            handleDialogClose();
        } catch (error) {
            if (error.status === 400) {
                if (error.response.data.unmatched.length > 0) {
                    error.response.data.unmatched.forEach((element) => {
                        toast.error(element.buyerId + ' : Not In Tipalti');
                    });
                } else if (error.response.data.unpayable.length > 0) {
                    error.response.data.unpayable.forEach((element) => {
                        toast.error(element.refCode + ' : Incomplete Tipalti Setup');
                    });
                } else {
                    toast.error('Payment Server Error');
                }
            } else {
                toast.error('Payment Server Error');
            }
        } finally {
            setBrokerLoading(false);
        }
    };

    const fetchBatches = async () => {
        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            const response = await api.get(
                `${process.env.REACT_APP_BASEURL}/api/batches/all`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setBatches(response.data);
        } catch (error) {
            console.error('Error fetching batches:', error);
            toast.error('Error fetching batches.');
        }
    };

    const getData = async (controller) => {
        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            const response = await api.get(
                `${process.env.REACT_APP_BASEURL}/api/datagrid/commissions/2024-10-14`,
                { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } }
            );

            const data = response.data.map((item, index) => ({
                id: index,
                paystart: dayjs(item._id.period_start.replace(/[Z]$/g, '')).toDate(),
                payend: dayjs(item._id.period_end_date.replace(/[Z]$/g, '')).toDate(),
                paydate: dayjs(item._id.pay_date.replace(/[Z]$/g, '')).toDate(),
                buyerId: item._id.buyer_broker_id,
                buyerName: item.bought_from_name,
                totalTicketCost: item.total_ticket_cost,
                totalConsignmentFees: item.total_consignment_fees_per_ticket,
                totalPoPayment: item.total_po_payment_per_ticket,
                totalPoBalanceDue: item.total_po_balance_due_per_ticket,
                purchaseOrders: item.purchase_orders || [] // Store purchase orders from API
            }));

            setData(data);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Error fetching data:', error);
                toast.error('Error fetching data.');
            }
        }
    };

    const createRowsAndColumns = () => {
        const rows = data.map((item, index) => {
            const status = getStatus(item);
            const amount = getAmount(item);
            const reconciliation = getReconciliation(item);
            return {
                id: index,
                paystart: item.paystart,
                payend: item.payend,
                paydate: item.paydate,
                buyerId: item.buyerId,
                buyerName: item.buyerName,
                totalTicketCost: item.totalTicketCost,
                totalConsignmentFees: item.totalConsignmentFees,
                totalPoPayment: item.totalPoPayment,
                totalPoBalanceDue: item.totalPoBalanceDue,
                purchaseOrders: item.purchaseOrders, // Include the purchaseOrders array
                status: status,
                amount: amount,
                reconStatus: reconciliation.status,
                reconciliation: reconciliation
            };
        });

        const columns = [
            { field: 'paystart', headerName: 'Pay Period Start', flex: .35, minWidth: 100, type: 'date' },
            { field: 'payend', headerName: 'Pay Period End', flex: .35, minWidth: 100, type: 'date' },
            { field: 'paydate', headerName: 'Pay Date', flex: .35, minWidth: 100, type: 'date' },
            { field: 'buyerId', headerName: 'Buyer/Payee ID', flex: .25, minWidth: 100, type: 'string' },
            { field: 'buyerName', headerName: 'Bought From Name', flex: .5, minWidth: 100, type: 'string' },
            {
                field: 'totalTicketCost', headerName: 'Ticket Cost', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${currencyFormatter(params.value)}`;
                }
            },
            {
                field: 'totalConsignmentFees', headerName: 'Commission', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${currencyFormatter(params.value)}`;
                }
            },
            {
                field: 'totalPoPayment', headerName: 'PO Payments', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${currencyFormatter(params.value)}`;
                }
            },
            {
                field: 'totalPoBalanceDue', headerName: 'PO Balance Due', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${currencyFormatter(params.value)}`;
                }
            },
            { field: 'status', headerName: 'Status', flex: .4, minWidth: 100, type: 'string' },
            {
                field: 'amount', headerName: 'Amount Sent', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${currencyFormatter(params.value)}`;
                }
            },
            {
                field: 'reconStatus', 
                headerName: 'Recon Status', 
                flex: .3, 
                minWidth: 120,
                renderCell: (params) => {
                    const status = params.value;
                    const hasBatch = params.row.reconciliation?.batch;
                    
                    // No batch associated - show nothing
                    if (!hasBatch) return '—';
                    
                    const statusConfig = {
                        'completed': { icon: <CheckCircleIcon fontSize="small" />, color: 'success', label: 'Completed' },
                        'partial': { icon: <WarningIcon fontSize="small" />, color: 'warning', label: 'Partial' },
                        'failed': { icon: <ErrorIcon fontSize="small" />, color: 'error', label: 'Failed' },
                        'in_progress': { icon: <HourglassEmptyIcon fontSize="small" />, color: 'info', label: 'In Progress' },
                        'pending': { icon: <HourglassEmptyIcon fontSize="small" />, color: 'info', label: 'Pending' },
                        'skipped': { icon: <SkipNextIcon fontSize="small" />, color: 'default', label: 'Skipped' },
                        'not_started': { icon: <LinkIcon fontSize="small" />, color: 'default', label: 'Not Started' },
                        'not_reconciled': { icon: <ErrorIcon fontSize="small" />, color: 'error', label: 'Not Reconciled' },
                        'N/A': { icon: <LinkIcon fontSize="small" />, color: 'default', label: 'Not Started' }
                    };
                    
                    const config = statusConfig[status] || { icon: <HelpOutlineIcon fontSize="small" />, color: 'default', label: status };
                    
                    return (
                        <Chip
                            size="small"
                            icon={config.icon}
                            label={config.label}
                            color={config.color}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleOpenReconDialog(params.row)}
                        />
                    );
                }
            }
        ];

        setRows(rows);
        setColumns(columns);
    };

    const getStatus = (item) => {
        let latestBatch = null;
        
        for (const batch of batches) {
            const requestData = batch.requestData.find(req =>
                dayjs(req.paystart).startOf('day').isSame(dayjs(item.paystart).startOf('day')) &&
                dayjs(req.payend).startOf('day').isSame(dayjs(item.payend).startOf('day')) &&
                dayjs(req.paydate).startOf('day').isSame(dayjs(item.paydate).startOf('day')) &&
                req.buyerId === item.buyerId
            );

            if (requestData) {
                if (!latestBatch || dayjs(batch.batchSummary.submissionDateTime).isAfter(dayjs(latestBatch.batchSummary.submissionDateTime))) {
                    latestBatch = batch;
                }
            }
        }

        if (latestBatch) {
            const matchedPayee = latestBatch.matchedPayees.find(payee => payee.refCode === item.buyerId.toString());
            if (matchedPayee) {
                const payment = latestBatch.payments.find(payment => payment.payeeId === matchedPayee.id);
                if (payment) {
                    return payment.status;
                }
            }
            return latestBatch.batchSummary.status;
        }

        return 'N/A';
    };

    const getAmount = (item) => {
        let latestBatch = null;

        for (const batch of batches) {
            const requestData = batch.requestData.find(req =>
                dayjs(req.paystart).startOf('day').isSame(dayjs(item.paystart).startOf('day')) &&
                dayjs(req.payend).startOf('day').isSame(dayjs(item.payend).startOf('day')) &&
                dayjs(req.paydate).startOf('day').isSame(dayjs(item.paydate).startOf('day')) &&
                req.buyerId === item.buyerId
            );

            if (requestData) {
                if (!latestBatch || dayjs(batch.batchSummary.submissionDateTime).isAfter(dayjs(latestBatch.batchSummary.submissionDateTime))) {
                    latestBatch = batch;
                }
            }
        }

        if (latestBatch) {
            const matchedPayee = latestBatch.matchedPayees.find(payee => payee.refCode === item.buyerId.toString());
            if (matchedPayee) {
                const payment = latestBatch.payments.find(payment => payment.payeeId === matchedPayee.id);
                if (payment) {
                    return payment.amountSubmitted.amount;
                }
            }
        }

        return 0;
    };

    // Get reconciliation status for a row
    const getReconciliation = (item) => {
        let latestBatch = null;
        
        for (const batch of batches) {
            const requestData = batch.requestData.find(req =>
                dayjs(req.paystart).startOf('day').isSame(dayjs(item.paystart).startOf('day')) &&
                dayjs(req.payend).startOf('day').isSame(dayjs(item.payend).startOf('day')) &&
                dayjs(req.paydate).startOf('day').isSame(dayjs(item.paydate).startOf('day')) &&
                req.buyerId === item.buyerId
            );

            if (requestData) {
                if (!latestBatch || dayjs(batch.batchSummary.submissionDateTime).isAfter(dayjs(latestBatch.batchSummary.submissionDateTime))) {
                    latestBatch = batch;
                }
            }
        }

        if (latestBatch && latestBatch.reconciliation) {
            // Check if THIS SPECIFIC BUYER's POs were actually reconciled
            // Look in reconciliation.results for this buyer
            const buyerResults = latestBatch.reconciliation.results?.filter(
                result => result.buyerId === item.buyerId
            ) || [];
            
            let buyerStatus;
            if (buyerResults.length === 0) {
                // This buyer was NOT processed in the reconciliation
                buyerStatus = 'not_reconciled';
            } else {
                // Check the status of their POs
                const allSuccess = buyerResults.every(r => r.status === 'success');
                const anySuccess = buyerResults.some(r => r.status === 'success');
                const anyFailed = buyerResults.some(r => r.status === 'failed');
                
                if (allSuccess) {
                    buyerStatus = 'completed';
                } else if (anyFailed) {
                    buyerStatus = anySuccess ? 'partial' : 'failed';
                } else {
                    buyerStatus = 'partial';
                }
            }
            
            return {
                status: buyerStatus,
                data: latestBatch.reconciliation,
                batch: latestBatch,
                buyerResults: buyerResults
            };
        }

        return { status: 'N/A', data: null, batch: latestBatch };
    };

    // Open reconciliation dialog - now works for any row with a batch
    const handleOpenReconDialog = (row) => {
        if (row.reconciliation?.batch) {
            setSelectedRecon({
                reconciliation: row.reconciliation.data || { status: 'not_started' },
                batch: row.reconciliation.batch
            });
            setReconDialogOpen(true);
        }
    };

    // Close reconciliation dialog
    const handleCloseReconDialog = () => {
        setReconDialogOpen(false);
        setSelectedRecon(null);
    };

    // Trigger reconciliation for the first time
    const handleTriggerReconciliation = async (batchId) => {
        setIsRetrying(true);
        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            await api.post(
                `${process.env.REACT_APP_BASEURL}/api/batches/${batchId}/reconciliation/trigger`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Reconciliation triggered successfully');
            handleCloseReconDialog();
            fetchBatches(); // Refresh data
        } catch (error) {
            toast.error('Trigger failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsRetrying(false);
        }
    };

    // Retry reconciliation
    const handleRetryReconciliation = async (batchId) => {
        setIsRetrying(true);
        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            await api.post(
                `${process.env.REACT_APP_BASEURL}/api/batches/${batchId}/reconciliation/retry`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Reconciliation retry initiated');
            handleCloseReconDialog();
            fetchBatches(); // Refresh data
        } catch (error) {
            toast.error('Retry failed: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsRetrying(false);
        }
    };

    // Detail panel handlers
    const getDetailPanelContent = useCallback(({ row }) => {
        return <PurchaseOrderDetailsGrid row={row} />;
    }, []);

    const getDetailPanelHeight = useCallback(() => 300, []);

    const handleDetailPanelExpandedRowIdsChange = useCallback((newIds) => {
        setExpandedRowIds(newIds);
    }, []);

    const isRowSelectable = (params) => {
        const selectableStatuses = ['N/A', 'CANCELED', 'FAILURE', 'REJECTED', 'REJECTED', 'DEFERRED', 'DEFERRED_INTERNAL'];
        return selectableStatuses.includes(params.row.status) && params.row.totalPoBalanceDue > 0;
    };

    useEffect(() => {
        const controller = new AbortController();

        if (isConnected) {
            fetchBatches();
            getData(controller);

            socket.on('data', () => { getData(controller); });
            socket.on('batches', fetchBatches);
        }

        return () => {
            socket.off('data');
            socket.off('batches', fetchBatches);
            controller.abort();
        };
    }, [isConnected]);

    useEffect(() => {
        if (data.length > 0) {
            createRowsAndColumns();
        } else {
            setRows([]);
            setColumns([]);
        }
    }, [data, batches]);

    if (user.admin) {
        if (columns !== null && rows !== null) {
            return (
                <>
                    <Backdrop
                        sx={{ color: '#fff', zIndex: 10000000 }}
                        open={broker_loading}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                    <Button onClick={handleDialogOpen}>
                        Send Selected Payments
                    </Button>
                    <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
                        <DialogContent>
                            <DialogContentText>Selected Payments:</DialogContentText>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Buyer ID</TableCell>
                                            <TableCell>Buyer Name</TableCell>
                                            <TableCell>Payment</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedRows.map((rowId) => {
                                            const row = rows.find((r) => r.id === rowId);
                                            return (
                                                <TableRow key={rowId}>
                                                    <TableCell>{row.buyerId}</TableCell>
                                                    <TableCell>{row.buyerName}</TableCell>
                                                    <TableCell>{currencyFormatter(row.totalPoBalanceDue)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleDialogClose}>Close</Button>
                            <Button variant="contained" color="primary" onClick={handleSubmit}>Submit</Button>
                        </DialogActions>
                    </Dialog>
                    <ReconciliationDialog
                        open={reconDialogOpen}
                        onClose={handleCloseReconDialog}
                        reconciliation={selectedRecon?.reconciliation}
                        batch={selectedRecon?.batch}
                        onRetry={handleRetryReconciliation}
                        onTrigger={handleTriggerReconciliation}
                        isRetrying={isRetrying}
                    />
                    <Paper elevation={1}>
                        <div style={{ height: '80vh', width: '100%' }}>
                            <DataGridPro
                                slots={{
                                    toolbar: GridToolbar
                                }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 }
                                    }
                                }}
                                rows={rows}
                                columns={columns}
                                pagination
                                checkboxSelection
                                disableRowSelectionOnClick
                                rowSelectionModel={selectedRows}
                                onRowSelectionModelChange={handleSelectionChange}
                                isRowSelectable={isRowSelectable}
                                getDetailPanelContent={getDetailPanelContent}
                                getDetailPanelHeight={getDetailPanelHeight}
                                detailPanelExpandedRowIds={expandedRowIds}
                                onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                            />
                        </div>
                    </Paper>
                </>
            );
        } else {
            return (
                <Box
                    component="main"
                    sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
                >
                    <CircularProgress size={60} thickness={4} />
                </Box>
            );
        }
    } else {
        return (
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
            >
                <WarningRoundedIcon size={60} thickness={4} />
                <p>Not Authorized</p>
            </Box>
        );
    }
}

export default Commissions;